// src/pages/settings/appointments/tabs/AppointmentSettingsTab.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Form, InputNumber, Select, Switch, Button, Space, Row, Col, Tooltip, Alert } from "antd";
import { SaveOutlined, ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";

// Shared components
import SectionCard from "../../../../components/shared/SectionCard";
import InlineLoading from "../../../../components/shared/InlineLoading";

// API
import { fetchAppointmentSettings, updateAppointmentSettings, resetAppointmentSettings } from "../../../../api/healthcare/Appointment/AppointmentSettingsApi";

// Types
import { CompanyAppointmentSettings } from "../../../../types/appointments/AppointmentSettingsTypes";

// Utils
import { extractValidationErrors } from "../../../../utils/apiError";

// Hooks
import { useNotifications } from "../../../../context/NotificationContext";

/**
 * `Intl.supportedValuesOf` is not in TypeScript 4.9's `lib`, so it is narrowed here rather than
 * cast to `any`. Every modern browser and Node 18+ implements it.
 */
type IntlWithSupportedValues = typeof Intl & {
	supportedValuesOf?: (key: "timeZone") => string[];
};

/**
 * The IANA zone list, sourced from the platform rather than hardcoded: the backend validates
 * `timezone` with Laravel's `timezone` rule (the full IANA database), and any hand-written short
 * list would drift away from it.
 *
 * `Australia/*` and `UTC` are surfaced first because this is an Australian clinical platform —
 * `showSearch` reaches the other ~400.
 */
const isPreferredZone = (timezone: string): boolean => timezone.startsWith("Australia/") || timezone === "UTC";

const IANA_TIMEZONES: string[] = (() => {
	// `supportedValuesOf` being present does not guarantee it won't throw on a given engine — and
	// this IIFE runs at MODULE scope, so an uncaught throw here fails the whole module import, not
	// just this tab: the entire settings page would white-screen. Fall back to an empty list (the
	// saved zone still stays selectable via `timezoneOptions` below) rather than letting that happen.
	try {
		const intl = Intl as IntlWithSupportedValues;
		const zones = typeof intl.supportedValuesOf === "function" ? intl.supportedValuesOf("timeZone") : [];

		return [...zones.filter(isPreferredZone), ...zones.filter((zone) => !isPreferredZone(zone))];
	} catch {
		return [];
	}
})();

const CALENDAR_SLOT_OPTIONS = [5, 10, 15, 20, 30].map((minutes) => ({ label: `${minutes} minutes`, value: minutes }));

const AppointmentSettingsTab: React.FC = () => {
	const [form] = Form.useForm<Partial<CompanyAppointmentSettings>>();
	const [loading, setLoading] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const [settings, setSettings] = useState<CompanyAppointmentSettings | null>(null);
	const { showSuccess, showError } = useNotifications();

	useEffect(() => {
		loadSettings();
	}, []);

	const loadSettings = async () => {
		setLoading(true);
		try {
			const data = await fetchAppointmentSettings();
			setSettings(data);
			form.setFieldsValue({ ...data });
		} catch (error) {
			showError("Failed to load appointment settings");
		} finally {
			setLoading(false);
		}
	};

	/**
	 * The saved zone always stays selectable, even if this browser's Intl does not list it (or does
	 * not implement `supportedValuesOf` at all) — otherwise opening the tab could quietly blank a
	 * timezone the clinic had already set.
	 */
	const timezoneOptions = useMemo(() => {
		const saved = settings?.timezone;
		const zones = saved && !IANA_TIMEZONES.includes(saved) ? [saved, ...IANA_TIMEZONES] : IANA_TIMEZONES;

		return zones.map((zone) => ({ label: zone, value: zone }));
	}, [settings?.timezone]);

	const handleSave = async () => {
		let values: Partial<CompanyAppointmentSettings>;

		// A client-side validation failure is already rendered inline by each Form.Item — it must not
		// be reported as a save failure, and must not reach the server-error branch below. antd only
		// ever rejects `validateFields()` with a `{ errorFields, values }` shape for that case — a
		// rejection WITHOUT `errorFields` is not a validation failure and must not be swallowed, or
		// the user gets zero feedback (no inline message, no toast) for a real failure.
		try {
			values = await form.validateFields();
		} catch (validationError) {
			if (validationError && typeof validationError === "object" && "errorFields" in validationError) {
				return;
			}

			showError("Failed to save settings", extractValidationErrors(validationError).join(" "));
			return;
		}

		try {
			setSaving(true);
			// `values` carries every field that has a Form.Item — including `timezone`, which had none
			// before S8 and so was dropped from every PUT by validateFields().
			const updated = await updateAppointmentSettings(values);
			setSettings(updated);
			form.setFieldsValue({ ...updated });
			showSuccess("Settings saved successfully");
		} catch (error) {
			// Surface the backend's 422 (e.g. an unknown timezone) rather than a generic failure.
			showError("Failed to save settings", extractValidationErrors(error).join(" "));
		} finally {
			setSaving(false);
		}
	};

	const handleReset = async () => {
		try {
			setLoading(true);
			const reset = await resetAppointmentSettings();
			setSettings(reset);
			form.setFieldsValue({ ...reset });
			showSuccess("Settings reset to defaults");
		} catch (error) {
			showError("Failed to reset settings", extractValidationErrors(error).join(" "));
		} finally {
			setLoading(false);
		}
	};

	if (loading && !settings) {
		return <InlineLoading minHeight={300} message="Loading settings…" />;
	}

	const saveActions = (
		<Space>
			<Button icon={<ReloadOutlined />} onClick={handleReset} loading={loading}>
				Reset to Defaults
			</Button>
			<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
				Save Changes
			</Button>
		</Space>
	);

	return (
		<Form form={form} layout="vertical">
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

				<SectionCard
					title="Booking Rules"
					subtitle="Configure how far ahead patients can book, how much notice you need, and the timezone slots are generated in"
					extra={saveActions}
				>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item
								label={
									<Space>
										Booking Window (Days)
										<Tooltip title="How many days ahead patients can book">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="booking_window_days"
								rules={[{ required: true, message: "Required" }]}
							>
								<InputNumber min={1} max={365} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								label={
									<Space>
										Minimum Notice (Hours)
										<Tooltip title="Minimum hours required before appointment">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="min_notice_hours"
								rules={[{ required: true, message: "Required" }]}
							>
								<InputNumber min={0} max={168} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								label={
									<Space>
										Timezone
										<Tooltip title="All appointment slots are generated in this timezone.">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="timezone"
								rules={[{ required: true, message: "Required" }]}
							>
								<Select
									showSearch
									placeholder="Select a timezone"
									style={{ width: "100%" }}
									filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
									options={timezoneOptions}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Allow Same Day Booking" name="allow_same_day_booking" valuePropName="checked">
								<Switch />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item label="Allow Cancellation" name="allow_cancellation" valuePropName="checked">
								<Switch />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								label={
									<Space>
										Cancellation Notice (Hours)
										<Tooltip title="How much notice a patient must give to cancel or reschedule online">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="cancellation_notice_hours"
								rules={[{ required: true, message: "Required" }]}
							>
								<InputNumber min={0} max={168} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							{/*
								S10 (ClickUp 86d3yhdwc). It MUST keep its own Form.Item — see the header
								comment above: validateFields() silently drops any field without one, and
								the save still reports success.
							*/}
							<Form.Item
								label={
									<Space>
										Booking link expiry (days)
										<Tooltip title="How long an issued patient booking link stays valid">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="booking_link_expiry_days"
								rules={[{ required: true, message: "Required" }]}
							>
								<InputNumber min={1} max={365} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard
					title="Buffer Times"
					subtitle="Add buffer time before and after each appointment, and choose how finely the calendar is divided"
				>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Buffer Before (Minutes)" name="buffer_before_minutes" rules={[{ required: true, message: "Required" }]}>
								<InputNumber min={0} max={120} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Buffer After (Minutes)" name="buffer_after_minutes" rules={[{ required: true, message: "Required" }]}>
								<InputNumber min={0} max={120} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							{/*
								S7b (ClickUp 86d436ve1). Only the divisors of 60 are offered — any other
								interval draws a grid that stops lining up with the hour labels.
							*/}
							<Form.Item
								label={
									<Space>
										Calendar grid interval
										<Tooltip title="How finely the calendar is divided. Affects how the calendar looks and how appointments snap while you drag them — it does not change any booking rule.">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="calendar_slot_minutes"
								rules={[{ required: true, message: "Required" }]}
								extra="Changing this re-draws the calendar only — no appointment is moved and nothing is saved differently. A 5-minute interval gives 144 rows across a 12-hour day, so it needs plenty of vertical space."
							>
								<Select
									style={{ width: "100%" }}
									options={CALENDAR_SLOT_OPTIONS}
								/>
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard
					title="Notifications"
					subtitle="Control which automated notifications are sent for appointments"
				>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item
								label={
									<Space>
										Send Booking Confirmation
										<Tooltip title="Whether to send the patient a booking confirmation. The notification template decides email vs SMS.">
											<InfoCircleOutlined style={{ color: "var(--mg-text-secondary)" }} />
										</Tooltip>
									</Space>
								}
								name="send_booking_confirmation"
								valuePropName="checked"
							>
								<Switch />
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>
			</div>
		</Form>
	);
};

export default AppointmentSettingsTab;
